import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { getValueType, safeStringify } from '../utils/jsonViewerUtils';
// プリミティブを型別に着色して描画する。
const Primitive = ({ value }) => {
    const type = getValueType(value);
    switch (type) {
        case 'string':
            return _jsx("span", { className: "text-green-700", children: JSON.stringify(value) });
        case 'number':
            return _jsx("span", { className: "text-blue-700", children: String(value) });
        case 'boolean':
            return _jsx("span", { className: "text-purple-700", children: String(value) });
        default:
            // null / undefined
            return _jsx("span", { className: "text-gray-400 italic", children: type });
    }
};
// 再帰ノード。object / array は折りたたみ可能、プリミティブは着色表示。
const JsonNode = ({ value, depth, defaultExpandedDepth, nodeKey, isArrayItem, ancestors, }) => {
    const type = getValueType(value);
    const isContainer = type === 'object' || type === 'array';
    const circular = isContainer && ancestors.has(value);
    const [expanded, setExpanded] = useState(depth < defaultExpandedDepth);
    const keyPrefix = nodeKey !== undefined && !isArrayItem ? (_jsxs("span", { className: "text-slate-600", children: ["\"", nodeKey, "\": "] })) : null;
    if (circular) {
        return (_jsxs("div", { children: [keyPrefix, _jsx("span", { className: "text-gray-400 italic", children: "[Circular]" })] }));
    }
    if (!isContainer) {
        return (_jsxs("div", { children: [keyPrefix, _jsx(Primitive, { value: value })] }));
    }
    const isArray = type === 'array';
    const open = isArray ? '[' : '{';
    const close = isArray ? ']' : '}';
    const entries = isArray
        ? value.map((v, i) => [String(i), v])
        : Object.entries(value);
    // 空の object / array はトグル無しでそのまま表示。
    if (entries.length === 0) {
        return (_jsxs("div", { children: [keyPrefix, _jsxs("span", { className: "text-gray-500", children: [open, close] })] }));
    }
    const childAncestors = new Set(ancestors);
    childAncestors.add(value);
    const summary = `${entries.length} ${isArray ? 'items' : 'keys'}`;
    return (_jsxs("div", { children: [_jsxs("button", { type: "button", onClick: () => setExpanded((e) => !e), className: "inline-flex items-center text-left hover:bg-gray-100 rounded px-0.5", children: [_jsx("span", { className: "text-gray-400 mr-1 select-none", children: expanded ? '▼' : '▶' }), keyPrefix, _jsx("span", { className: "text-gray-500", children: open }), !expanded && (_jsxs("span", { className: "text-gray-400", children: ["\u2026 ", summary, " ", close] }))] }), expanded && (_jsxs(_Fragment, { children: [_jsx("div", { className: "pl-4 ml-1 border-l border-gray-200", children: entries.map(([k, v]) => (_jsx(JsonNode, { value: v, depth: depth + 1, defaultExpandedDepth: defaultExpandedDepth, nodeKey: k, isArrayItem: isArray, ancestors: childAncestors }, k))) }), _jsx("div", { className: "text-gray-500", children: close })] }))] }));
};
// 構造化データ表示のための共通コンポーネント。
// 構文ハイライト + 折りたたみ + コピーを提供する。
const JsonViewer = ({ data, className = '', bare = false, defaultExpandedDepth = 2, copyable = true, }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(safeStringify(data, 2));
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        }
        catch {
            // navigator.clipboard 不在（非セキュアコンテキスト等）は no-op
        }
    };
    const boxed = bare ? '' : 'bg-gray-50 rounded border p-3';
    return (_jsxs("div", { className: `relative ${boxed} ${className}`, children: [copyable && (_jsx("button", { type: "button", onClick: handleCopy, className: "absolute top-1 right-1 z-10 px-2 py-0.5 text-xs rounded bg-white border border-gray-300 text-gray-600 hover:bg-gray-100", title: "\u30B3\u30D4\u30FC", children: copied ? 'コピー済み' : 'コピー' })), _jsx("div", { className: "font-mono text-xs leading-relaxed overflow-auto max-h-96", children: _jsx(JsonNode, { value: data, depth: 0, defaultExpandedDepth: defaultExpandedDepth, ancestors: new Set() }) })] }));
};
export default JsonViewer;
